import React, { useEffect, useState } from 'react';
import useLocationData from '../hooks/useLocationData';
import useLocationDetection from '../hooks/useLocationDetection';
import SearchableSelect from './Form/SearchableSelect';

const LocationDropdowns = ({ 
  selectedCountry, 
  selectedState, 
  selectedCity, 
  onCountryChange, 
  onStateChange, 
  onCityChange,
  disabled = false,
  showLabels = true,
  className = "",
  enableAutoDetection = true,
  showAutoDetectButton = true,
  showCountryDropdown = true,
  getFieldStyle = null // Optional function to get field styles for color coding
}) => {
  const {
    countries,
    states,
    cities,
    loading,
    error,
    lastUpdated,
    fetchStates,
    fetchCities,
    refreshAllData
  } = useLocationData();

  const {
    location: detectedLocation,
    loading: detectionLoading,
    error: detectionError,
    permissionStatus,
    detectLocation,
    requestPermission,
    clearCache
  } = useLocationDetection();

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [autoDetecting, setAutoDetecting] = useState(false);

  // Fetch states when country changes
  useEffect(() => {
    if (selectedCountry) {
      fetchStates(selectedCountry);
    } else {
      fetchStates(null);
    }
  }, [selectedCountry, fetchStates]);

  // Fetch cities when state changes
  useEffect(() => {
    if (selectedState) {
      fetchCities(selectedState);
    } else {
      fetchCities(null);
    }
  }, [selectedState, fetchCities]);

  // Handle country change
  const handleCountryChange = (e) => {
    const countryId = e.target.value;
    onCountryChange(countryId);
    // Reset dependent dropdowns
    onStateChange('');
    onCityChange('');
  };

  // Handle state change
  const handleStateChange = (e) => {
    const stateId = e.target.value;
    onStateChange(stateId);
    // Reset city dropdown
    onCityChange('');
  };

  // Handle city change
  const handleCityChange = (e) => {
    const cityId = e.target.value;
    onCityChange(cityId);
  };

  // Handle refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshAllData();
    } finally {
      setIsRefreshing(false);
    }
  };

  // Handle auto-detection
  const handleAutoDetect = async () => {
    setAutoDetecting(true);
    try {
      // Force refresh and use IP location (more reliable)
      const location = await detectLocation({
        useDeviceLocation: false,
        useIPLocation: true,
        forceRefresh: true
      });
      
      console.log('📍 Auto-detected location:', location);
      
      if (location && location.databaseMatch && location.databaseMatch.matched) {
        const { country, state, city } = location.databaseMatch;
        
        // Set the detected location
        if (country) {
          onCountryChange(country.id);
          console.log('📍 Set country:', country);
        }
        if (state) {
          onStateChange(state.id);
          console.log('📍 Set state:', state);
        }
        if (city) {
          onCityChange(city.id);
          console.log('📍 Set city:', city);
        }
      } else {
        console.warn('📍 Location detected but no database match found:', location);
        // Still try to set country/state from detected location if available
        if (location) {
          // Try to find country by code
          const countryCode = location.countryCode;
          if (countryCode && countries.length > 0) {
            const matchedCountry = countries.find(c => c.code === countryCode || c.id === countryCode);
            if (matchedCountry) {
              onCountryChange(matchedCountry.id);
              console.log('📍 Set country from code:', matchedCountry);
            }
          }
        }
      }
    } catch (error) {
      console.error('📍 Auto-detection failed:', error);
      // Error is already set in the hook, just log it
    } finally {
      setAutoDetecting(false);
    }
  };

  // Handle permission request
  const handleRequestPermission = async () => {
    try {
      await requestPermission();
    } catch (error) {
      console.error('Permission request failed:', error);
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header with controls */}
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-gray-700 flex items-center">
          📍 Location Information
          {lastUpdated && (
            <span className="text-xs text-gray-500 ml-2">
              (Updated: {lastUpdated.toLocaleTimeString()})
            </span>
          )}
        </h3>
        <div className="flex gap-2">
          {showAutoDetectButton && enableAutoDetection && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('📍 Auto-detect button clicked');
                handleAutoDetect();
              }}
              disabled={autoDetecting || detectionLoading || disabled}
              className="text-xs px-2 py-1 bg-green-100 text-green-600 rounded hover:bg-green-200 disabled:opacity-50 cursor-pointer"
              title="Auto-detect your location"
            >
              {autoDetecting || detectionLoading ? '🔄 Detecting...' : '📍 Auto-detect'}
            </button>
          )}
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isRefreshing || loading}
            className="text-xs px-2 py-1 bg-blue-100 text-blue-600 rounded hover:bg-blue-200 disabled:opacity-50"
          >
            {isRefreshing ? '🔄' : '↻'} Refresh
          </button>
        </div>
      </div>

      {/* Error messages */}
      {error && (
        <div className="text-red-600 text-sm bg-red-50 p-2 rounded">
          ⚠️ {error}
        </div>
      )}
      
      {detectionError && (
        <div className="text-orange-600 text-sm bg-orange-50 p-2 rounded">
          ⚠️ Auto-detection: {detectionError}
        </div>
      )}
      
      {autoDetecting && (
        <div className="text-blue-600 text-sm bg-blue-50 p-2 rounded">
          🔄 Detecting your location...
        </div>
      )}

      {/* Permission status */}
      {permissionStatus === 'denied' && (
        <div className="text-yellow-600 text-sm bg-yellow-50 p-2 rounded">
          ℹ️ Location permission denied. Auto-detection will use IP-based location.
        </div>
      )}

      {/* Country Dropdown */}
      {showCountryDropdown && (
        <div>
          {showLabels && (
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Country *
            </label>
          )}
          <SearchableSelect
            name="country"
            value={selectedCountry || ''}
            onChange={handleCountryChange}
            disabled={disabled || loading}
            placeholder="Select Country"
            options={countries}
            getOptionLabel={(country) =>
              `${country.name}${country.code ? ` (${country.code})` : ''}`
            }
            getOptionValue={(country) => country.id}
          />
          {loading && <div className="text-xs text-gray-500 mt-1">Loading countries...</div>}
        </div>
      )}

      {/* State Dropdown */}
      <div>
        {showLabels && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            State {!selectedCountry && <span className="text-gray-400">(Select country first)</span>}
          </label>
        )}
        <SearchableSelect
          name="state"
          value={selectedState || ''}
          onChange={handleStateChange}
          disabled={disabled || !selectedCountry || loading}
          placeholder="Select State"
          options={states}
          getOptionLabel={(state) =>
            `${state.name}${state.code ? ` (${state.code})` : ''}`
          }
          getOptionValue={(state) => state.id}
        />
        {loading && selectedCountry && <div className="text-xs text-gray-500 mt-1">Loading states...</div>}
      </div>

      {/* City Dropdown */}
      <div>
        {showLabels && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            City {!selectedState && <span className="text-gray-400">(Select state first)</span>}
          </label>
        )}
        <SearchableSelect
          name="cityId"
          value={selectedCity || ''}
          onChange={handleCityChange}
          disabled={disabled || !selectedState || loading}
          placeholder="Select City"
          options={cities}
          getOptionLabel={(city) => city.name}
          getOptionValue={(city) => city.id}
        />
        {loading && selectedState && <div className="text-xs text-gray-500 mt-1">Loading cities...</div>}
      </div>

      {/* Debug info (only in development) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="text-xs text-gray-400 bg-gray-50 p-2 rounded">
          <div>Countries: {countries.length}</div>
          <div>States: {states.length}</div>
          <div>Cities: {cities.length}</div>
        </div>
      )}
    </div>
  );
};

export default LocationDropdowns;
